import React, { useState } from 'react';

import { Field, FieldProps, Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import * as Yup from 'yup';

import { useRegistration } from '@/hooks/useRegistration';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { MobileInputModel } from '@/models/mobile-input.model';
import { MobileInput } from '@/components/MobileInput';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { BtnBlueLink } from '@/components/buttons/BtnBlueLink';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { useSelector } from 'react-redux';
import { BffAuthRequest, BffAuthResponse, BlockReasonType } from '@lucky7ventures/bff-types';
import { selectIsCanadaMarket } from '@/store/selectors/commonSelectors';
import { ButtonWithLoader } from '@/components/Common/ButtonWithLoader';
import { FormPassword } from '@/components/FormPassword';
import PromptMessage from '@/components/UI/PromptMessage';

interface LoginForm {
  mobile: MobileInputModel;
  password: string;
}

export function LoginSmsFirstStep({
  onSuccessfulLogin,
}: {
  onSuccessfulLogin: (data: BffAuthResponse, payload: BffAuthRequest) => void;
}): JSX.Element {
  const intl = useIntl();
  const { openModal, closeModal } = useModal();
  const { loading, error, request } = useBffApiRequest<BffAuthResponse>();
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [errorMessage, setErrorMessage] = useState<React.ReactNode | null>(null);
  const isCanadaMarket = useSelector(selectIsCanadaMarket);
  const { openRegistration, isRegistrationRemoved } = useRegistration();
  const navigate = useAbsoluteNavigate();

  const loginSchema = Yup.object().shape({
    mobile: Yup.object().shape({
      wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string().min(5, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
    password: Yup.string()
      .required(intl.formatMessage({ id: 'inputs.password.required' }))
      .min(6, intl.formatMessage({ id: 'inputs.password.invalid' })),
  });

  const submitHandler = (values: LoginForm) => {
    setErrorMessage(null);
    const payload = { username: values.mobile.wholeNumber, password: values.password };

    const requestCallback = (data: BffAuthResponse) => {
      if (isCanadaMarket && data.blockReason?.blockReasonType === BlockReasonType.Timeout) {
        setErrorMessage(intl.formatMessage({ id: 'login.error.timeout' }));
        return;
      }

      onSuccessfulLogin(data, payload);
    };

    const errorCallback = (error: BffApiError) => {
      setLoginAttempts(prevState => prevState + 1);

      if (loginAttempts > 1) {
        openModal(MODALS.FAILED_LOGIN_SMS);
      }

      triggerGTag(GTagEvents.login_sms_error, {
        errorId: error.code,
      });
    };
    request({
      apiMethod: BffApiService.login,
      payload,
      errorCallback: errorCallback,
      successCallback: requestCallback,
    });
  };

  const onForgotPasswordClicked = () => {
    closeModal(MODALS.LOGIN_SMS);
    navigate('/forgot-password-sms');
  };

  const onSwitchToEmailLogin = () => {
    triggerGTag(GTagEvents.login_modal_switch_email);
    openModal(MODALS.LOGIN);
  };

  return (
    <div>
      {(error || errorMessage) && (
        <div className="-mt-4">
          <PromptMessage
            type="error"
            message={errorMessage || intl.formatMessage({ id: 'error.1' })}
          />
        </div>
      )}
      <Formik
        initialValues={{
          mobile: {
            number: '',
            country: 'TH',
            prefix: '+66',
            wholeNumber: '',
          },
          password: '',
        }}
        validationSchema={loginSchema}
        onSubmit={values => submitHandler(values)}
      >
        {({ isValid }) => (
          <Form>
            <Field name="mobile">
              {({ field, form }: FieldProps) => (
                <MobileInput field={field} form={form} {...field} />
              )}
            </Field>
            <FormPassword
              data-cy="login-modal-password"
              id="password"
              name="password"
              label={intl.formatMessage({ id: 'inputs.password' })}
              labelLink={{
                text: intl.formatMessage({ id: 'inputs.password.forgot' }),
                onClick: () => onForgotPasswordClicked(),
              }}
              placeholder={intl.formatMessage({
                id: 'inputs.password.placeholder',
              })}
            />
            <ButtonWithLoader
              loading={loading}
              message={intl.formatMessage({ id: 'login.login' })}
              disabled={!isValid || loading}
            />
          </Form>
        )}
      </Formik>
      <div className="grid grid-cols-1 gap-4">
        {!isRegistrationRemoved && (
          <>
            <div className="text-sm font-bold text-center">
              {intl.formatMessage({ id: 'login.createAccountText' })}{' '}
              <button
                type="button"
                className="cursor-pointer font-bold text-blue-medium underline transition hover:text-blue-light"
                onClick={() => {
                  closeModal(MODALS.LOGIN_SMS);
                  openRegistration({ openedFrom: OpenedFrom.LoginFirstStep });
                }}
              >
                {intl.formatMessage({ id: 'login.createAccountLink' })}
              </button>
            </div>
            <div className="h-[1px] bg-slate-400 w-full opacity-60"></div>
          </>
        )}
        <BtnBlueLink
          data-cy="login-form-switch-login-via-email"
          onClickHandler={onSwitchToEmailLogin}
          text={intl.formatMessage({ id: 'authentication.login.via-email' })}
        />
      </div>
    </div>
  );
}
